/**
 * Cold Call Mastery Scenario — Improved Edition (First 20 Characters)
 * 
 * Changes:
 * - Removed famous names (Rachel Zane → Claire Donovan, Greg Halpert → David Park, 
 *   Angela Martin → Helen Torres, Chloe Decker → Zoe Mitchell, Brian Dawkins → Marcus Reid)
 * - Added silenceProfile per character for realistic hangup behavior
 * - Rewrote all lines to be more natural, distinct, and character-specific
 * - Expanded scenarioDesc and coachingNotes for depth
 * 
 * silenceProfile.stages[] controls hangup timing:
 *   - type: "warn" | "hangup"
 *   - delay: milliseconds of silence before triggering
 *   - lineKey: key in character.lines to speak
 *   - (audio is looked up by lineKey: see ../audio.js and audioManifest.json)
 */

// CASTLE ATLAS — Cold Call Mastery
// 45 characters | Schema version: unified (matches NT/OD/EC)
// Updated: May 2026

export const ALL_CHARACTERS = {

  // ═════════════════════════════════════════════════════════════════
  // 1. HENRY DAVIES — The Suspicious Retiree
  // ═════════════════════════════════════════════════════════════════
  'henry-davies-retired-hobbyist-gardener': {
    id: 'henry-davies-retired-hobbyist-gardener',
    name: 'Henry Davies',
    role: 'Retired Hobbyist Gardener',
    company: 'Homeowner',
    industry: 'consumer',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/henry-davies-retired-hobbyist-gardener.webp',
    difficulty: 'beginner',
    color: '#6b7280',
    age: 77,
    gender: 'male',
    ethnicity: 'Welsh',
    tags: ['skeptical', 'consumer', 'retiree'],
    scenarioTitle: 'The Suspicious Retiree',
    scenarioDesc: "Henry is 77, Welsh, retired about 15 years. A keen gardener, he spends his days out in the garden and has just come in from it, still in his muddy boots. He picks up the phone thinking it's his son. He hates telemarketers and has been scammed before. He gives strangers exactly zero benefit of the doubt. If you hesitate, he assumes you're reading a script and hangs up immediately.",
    goal: 'Get him to stay on the line for 2 more minutes.',
    category: 'cold-call-mastery',
    personalityType: 'skeptical',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'instant',
      stages: [
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Hello? Who's this then? I'm not buying anything, I'll tell you that now.",
      silence_1: "This is one of those scam calls, isn't it? My son warned me about you lot.",
      silence_2: "Right, that's enough of that.",
      hangup: "Right. I'm putting this phone down now. [muttering] Bloody telemarketers... *click*", 
    },
    coachingNotes: {
      whatWorks: "Acknowledge you know he's suspicious. Say 'I don't blame you, most of these calls are rubbish' — it disarms him instantly. Speak within 3 seconds of him answering or he hangs up.",
      commonMistake: "Hesitating after he speaks. Silence makes him think you're a robocall or reading a script. He hangs up at 10 seconds of dead air, no warnings.",
      winningMove: "Say: 'Mr Davies, I promise I'm not selling you a thing today. I just have one question about your garden that takes 30 seconds. Can I ask it?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 2. MARIA SANTOS — The Rushed Owner
  // ═════════════════════════════════════════════════════════════════
  'maria-santos-cafe-owner': {
    id: 'maria-santos-cafe-owner',
    name: 'Maria Santos',
    role: 'Café Owner',
    company: "Maria's Neighborhood Café",
    industry: 'food & beverage',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/maria-santos-cafe-owner.webp',
    difficulty: 'beginner',
    color: '#d97706',
    age: 42,
    gender: 'female',
    ethnicity: 'Latina',
    tags: ['rushed', 'food-bev', 'owner'],
    scenarioTitle: 'The Rushed Owner',
    scenarioDesc: "Maria is 42, Latina, running the counter herself during morning rush. She picked up the phone while pulling an espresso. She's friendly but absolutely slammed. Her patience is measured in seconds, not minutes. Background noise: espresso machine, customers, register beeping.",
    goal: 'Book a 10-minute call for later today.',
    category: 'cold-call-mastery',
    personalityType: 'rushed',
    objectionStyle: 'no-time',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 3000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 7000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "[espresso machine hissing, register beeping] Maria's — hold on, grande oat milk — sorry, what did you need?",
      silence_1: "I'm so sorry, I've got a line to the door. Can you call me after 2?",
      silence_2: "I feel terrible but I really can't do this right now. I'm sorry, I'm sorry.",
      hangup: "I have to go, I'm so sorry. Call me at 3, I promise I'll pick up. *click*", 
    },
    coachingNotes: {
      whatWorks: "Hear the chaos and acknowledge it immediately. Offer to call back at a specific time — give her a choice of two slots. Speak fast.",
      commonMistake: "Trying to pitch during morning rush. She's physically incapable of listening properly right now. Also: pausing for more than 3 seconds makes her hang up.",
      winningMove: "Say: 'I can hear you're slammed. Can I call you back at 2pm or would 4pm work better? It's a 10-minute call, that's it.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 3. AHMED HASSAN — The Annoyed Creative
  // ═════════════════════════════════════════════════════════════════
  'ahmed-hassan-freelance-graphic-designer': {
    id: 'ahmed-hassan-freelance-graphic-designer',
    name: 'Ahmed Hassan',
    role: 'Freelance Graphic Designer',
    company: 'Self-employed',
    industry: 'creative',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/ahmed-hassan-freelance-graphic-designer.webp',
    difficulty: 'beginner',
    color: '#7c3aed',
    age: 31,
    gender: 'male',
    ethnicity: 'Egyptian',
    tags: ['creative', 'distracted', 'freelancer'],
    scenarioTitle: 'The Annoyed Creative',
    scenarioDesc: "Ahmed is 31, Egyptian, working from home deep in a client project. He's got headphones around his neck and answered the phone by mistake thinking it was a delivery. He's mildly annoyed, doesn't suffer fools, and has zero patience for corporate speak. He can smell a script from the first sentence.",
    goal: 'Get him to agree to look at a one-page PDF.',
    category: 'cold-call-mastery',
    personalityType: 'distracted',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 4000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 9000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Yeah? [keyboard clicking stops] Who is this? I'm in the middle of a client mockup.",
      silence_1: "Dude, I just lost my flow state for this. What are you actually selling?",
      silence_2: "I really don't need this right now.",
      hangup: "Cool. Good luck with... whatever this is. *click*", 
    },
    coachingNotes: {
      whatWorks: "Be direct and creative. Reference the specific pain of freelancers — chasing invoices, client revisions, finding new work. Pick one. No corporate fluff.",
      commonMistake: "Generic pitch or silence. He's heard every generic pitch. It insults his intelligence. And silence makes him assume you're a bot.",
      winningMove: "Say: 'I'll be honest — I'm calling freelancers specifically. Do you spend more than 3 hours a week on admin that has nothing to do with your actual work?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 4. LINDA OKAFOR — The Patient Listener
  // ═════════════════════════════════════════════════════════════════
  'linda-okafor-retired-schoolteacher': {
    id: 'linda-okafor-retired-schoolteacher',
    name: 'Linda Okafor',
    role: 'Retired Schoolteacher',
    company: 'Homeowner',
    industry: 'consumer',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/linda-okafor-retired-schoolteacher.webp',
    difficulty: 'beginner',
    color: '#059669',
    age: 65,
    gender: 'female',
    ethnicity: 'Nigerian-British',
    tags: ['patient', 'consumer', 'teacher'],
    scenarioTitle: 'The Patient Listener',
    scenarioDesc: "Linda is 65, Nigerian-British, sitting in her living room with a book. She'll hear you out fully — but she asks sharp questions and won't be pressured. She raised three kids and ran a classroom for 30 years. She doesn't rattle easily. She gives people time because she believes in fairness, but she has limits.",
    goal: 'Get a firm commitment to a follow-up call this week.',
    category: 'cold-call-mastery',
    personalityType: 'polite-but-firm',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'patient',
      stages: [
        { type: 'warn', delay: 8000, lineKey: 'silence_1' },
        { type: 'warn', delay: 16000, lineKey: 'silence_2' },
        { type: 'warn', delay: 20000, lineKey: 'lastChance' },
        { type: 'hangup', delay: 24000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Hello, Linda speaking. I'm listening, go ahead.",
      silence_1: "Now then. I'm still here, but you need to be clearer. What exactly is the proposition?",
      silence_2: "I appreciate your effort, but you're not answering my question. Let me ask again...",
      lastChance: "I'm going to give you one more opportunity to explain this properly. Take your time.",
      hangup: "I think we've run out of road here. You're welcome to try again when you've prepared better. Goodbye."
    },
    coachingNotes: {
      whatWorks: "Give her specifics. She was a teacher — vague claims insult her. Give her a real number, a real case, a real result. She gives you up to 24 seconds of silence before hanging up.",
      commonMistake: "Emotional appeals or urgency tactics. She will see straight through them and lose respect for you.",
      winningMove: "Say: 'Mrs Okafor, I'll give you one specific example. A retired teacher in Bristol saved £340 last year doing exactly what I'm going to describe. Can I take 2 minutes?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 5. GREG THOMPSON — The Blunt Road Warrior
  // ═════════════════════════════════════════════════════════════════
  'greg-thompson-long-haul-truck-driver': {
    id: 'greg-thompson-long-haul-truck-driver',
    name: 'Greg Thompson',
    role: 'Long-Haul Truck Driver',
    company: 'Self-employed contractor',
    industry: 'transport',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/greg-thompson-long-haul-truck-driver.webp',
    difficulty: 'beginner',
    color: '#b45309',
    age: 52,
    gender: 'male',
    ethnicity: 'American',
    tags: ['direct', 'transport', 'driver'],
    scenarioTitle: 'The Blunt Road Warrior',
    scenarioDesc: "Greg is 52, American, sitting at a roadside diner booth with a coffee. He's between routes and answered because he had a minute. He's direct, hates small talk, and will tell you exactly what he thinks. He respects people who get to the point and despises anyone who beats around the bush.",
    goal: 'Earn 2 more minutes of his time.',
    category: 'cold-call-mastery',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "[diner ambient — clattering plates] Yeah, who's this? I got about ten minutes before I'm wheels up.",
      silence_1: "Alright, how much? Don't give me the runaround, just give me the number.",
      silence_2: "Look, I've got a route to run. What's the bottom line here?",
      hangup: "Yeah, I'll pass. [to waitress] Can I get the check? [to you] Good luck, bud. *click*", 
    },
    coachingNotes: {
      whatWorks: "Match his energy. Be blunt back. Don't dress it up. Give him a number and a result in the same breath.",
      commonMistake: "Any kind of corporate or sales-speak. He will hang up immediately. Silence makes him think you're fishing for a script.",
      winningMove: "Say: 'It's $49 a month. Guys like you typically save that in the first week by cutting one bad load decision. Want to hear how?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 6. JENNY WALSH — The Frazzled Multi-tasker
  // ═════════════════════════════════════════════════════════════════
  'jenny-walsh-stay-at-home-mom': {
    id: 'jenny-walsh-stay-at-home-mom',
    name: 'Jenny Walsh',
    role: 'Stay-at-Home Mom',
    company: 'Homeowner',
    industry: 'consumer',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/jenny-walsh-stay-at-home-mom.webp',
    difficulty: 'beginner',
    color: '#db2777',
    age: 36,
    gender: 'female',
    ethnicity: 'Irish-American',
    tags: ['distracted', 'consumer', 'parent'],
    scenarioTitle: 'The Frazzled Multi-tasker',
    scenarioDesc: "Jenny is 36, Irish-American, at her kitchen island with a coffee. She has three kids, the youngest is napping. She answered because she thought it might be the school. She'll give you 30 seconds tops. Her attention is split between the call, the baby monitor, and a toddler who could wake up any second.",
    goal: 'Get her to commit to a specific next step.',
    category: 'cold-call-mastery',
    personalityType: 'distracted',
    objectionStyle: 'no-time',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 3000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 6000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "[whispered] Hello? Oh — you're not the school. [normal voice] Who is this? Make it quick, the baby just went down.",
      silence_1: "[whispered urgently] Shh shh — you need to talk quieter or call back. She's stirring.",
      silence_2: "I really don't have time for this right now.",
      hangup: "[baby crying in background] Oh no. I have to go. Just — send me an email or something. *click*", 
    },
    coachingNotes: {
      whatWorks: "Be warm, be fast, be specific. Acknowledge her situation. Give her one clear thing to do — not a pitch, a next step. You have 6 seconds of silence max.",
      commonMistake: "Any sentence longer than 15 words. She's literally holding a baby monitor. Also: silence makes her assume it's a robocall and she hangs up instantly.",
      winningMove: "Say: 'I'll be super quick. I'm not selling anything today. Can I send you a two-paragraph email so you can read it when the kids are asleep?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 7. TOMAS NOVAK — The Wary Creative
  // ═════════════════════════════════════════════════════════════════
  'tomas-novak-part-time-musician-and-warehouse-worker': {
    id: 'tomas-novak-part-time-musician-and-warehouse-worker',
    name: 'Tomas Novak',
    role: 'Part-time Musician & Warehouse Worker',
    company: 'Self-employed / Day job',
    industry: 'creative',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/tomas-novak-part-time-musician-and-warehouse-worker.webp',
    difficulty: 'beginner',
    color: '#4f46e5',
    age: 28,
    gender: 'male',
    ethnicity: 'Czech',
    tags: ['guarded', 'creative', 'musician'],
    scenarioTitle: 'The Wary Creative',
    scenarioDesc: "Tomas is 28, Czech, sitting on his couch with a guitar leaning behind him. He's between shifts and suspicious of anything that smells like a scam or MLM. He's broke-ish and wary but not rude. He gives you one chance to prove you're human and relevant.",
    goal: 'Get him to agree to receive a one-page case study.',
    category: 'cold-call-mastery',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Hello? Yeah, speaking. What do you want?",
      silence_1: "Wait — is this one of those 'be your own boss' things? Or crypto? Because I'm not doing crypto.",
      silence_2: "I don't really have money to spend on stuff right now.",
      hangup: "Yeah, this sounds like every other pitch my mates get on Instagram. No thanks. *click*", 
    },
    coachingNotes: {
      whatWorks: "Be honest that you're selling something. Paradoxically, honesty disarms the guard. Then make it relevant to musicians specifically.",
      commonMistake: "Sounding like a telemarketer or going silent. The moment you sound scripted or pause too long, he's gone.",
      winningMove: "Say: 'I'm not going to pretend this isn't a sales call. But I work specifically with musicians who are trying to grow gigs. Is that something you're working on?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 8. DOROTHY FISCHER — The Trusting Elder
  // ═════════════════════════════════════════════════════════════════
  'dorothy-fischer-retired-widow': {
    id: 'dorothy-fischer-retired-widow',
    name: 'Dorothy Fischer',
    role: 'Retired Widow',
    company: 'Homeowner',
    industry: 'consumer',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/dorothy-fischer-retired-widow.webp',
    difficulty: 'beginner',
    color: '#9d174d',
    age: 74,
    gender: 'female',
    ethnicity: 'German-American',
    tags: ['trusting', 'consumer', 'elder'],
    scenarioTitle: 'The Trusting Elder',
    scenarioDesc: "Dorothy is 74, German-American, sitting at her kitchen table with a landline. She's polite to everyone who calls — sometimes too polite. She's been targeted by scammers before and her daughter told her to be careful. She's nervous but won't hang up on people quickly. She gives you the benefit of the doubt.",
    goal: 'Earn her trust and get her to agree to a follow-up call with her daughter present.',
    category: 'cold-call-mastery',
    personalityType: 'polite-but-firm',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'patient',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'silence_1' },
        { type: 'warn', delay: 14000, lineKey: 'silence_2' },
        { type: 'warn', delay: 18000, lineKey: 'lastChance' },
        { type: 'hangup', delay: 22000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Hello? Oh, good afternoon. Who am I speaking with, please?",
      silence_1: "You know, my neighbor Margaret lost $2,000 on a phone call just like this one. My daughter says I should always get things in writing.",
      silence_2: "I don't really like to make decisions on the phone with strangers. It's nothing personal.",
      lastChance: "I'm trying to be polite, but I really do need you to be more specific about what you're offering.",
      hangup: "I really should check with my daughter first. She's very good with these things. Thank you for being patient with me. *click*", 
    },
    coachingNotes: {
      whatWorks: "Suggest involving her daughter. It's the one thing that actually builds trust with Dorothy — you're not trying to hide anything. She tolerates up to 22 seconds of silence.",
      commonMistake: "Pressuring her to decide on the call. It will terrify her and she will hang up.",
      winningMove: "Say: 'Mrs Fischer, I think that's a wonderful idea. Could we schedule a 10-minute call with you and your daughter together? I'll send everything in writing first.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 9. DEREK BROWN — The Digital Native
  // ═════════════════════════════════════════════════════════════════
  'derek-brown-college-student-and-part-time-barista': {
    id: 'derek-brown-college-student-and-part-time-barista',
    name: 'Derek Brown',
    role: 'College Student & Part-time Barista',
    company: 'University / Coffee shop',
    industry: 'consumer',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/derek-brown-college-student-and-part-time-barista.webp',
    difficulty: 'beginner',
    color: '#0891b2',
    age: 21,
    gender: 'male',
    ethnicity: 'African-American',
    tags: ['skeptical', 'consumer', 'student'],
    scenarioTitle: 'The Digital Native',
    scenarioDesc: "Derek is 21, African-American, sitting cross-legged with his laptop open. He answered out of mild amusement — he's never gotten a cold call before. He'll give you exactly as long as it takes him to decide you're cringe. Gen Z attention span: extremely short. Silence = instant hangup.",
    goal: 'Keep him engaged long enough to earn a follow-up.',
    category: 'cold-call-mastery',
    personalityType: 'skeptical',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 4000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 8000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Uh... hello? Wait, is this a real phone call? Like from a person? I literally never get these.",
      silence_1: "Okay hold on, I need to screenshot this for my story. A real cold call. This is wild. Go ahead though, what's the pitch?",
      silence_2: "Okay I respect the hustle but I'm a student making $14 an hour, so like... unless it's free?",
      hangup: "Yo this was actually kind of fun but I gotta go to class. Good luck with the whole calling-people thing. *click*", 
    },
    coachingNotes: {
      whatWorks: "Be self-aware and slightly funny. Acknowledge the awkwardness of cold calling a student. Make it feel like a real conversation, not a script.",
      commonMistake: "Sounding corporate or formal. You will lose him in 4 seconds flat. Silence makes him think the call dropped or you're a scammer.",
      winningMove: "Say: 'Okay real talk — I know this is a weird call to get. I'm not trying to sell you anything today. I just want to ask you one question and if the answer is no, I'll hang up. Sound fair?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 10. RAJ KUMAR — The Hustling Side-Gig Guy
  // ═════════════════════════════════════════════════════════════════
  'raj-kumar-rideshare-driver-and-online-seller': {
    id: 'raj-kumar-rideshare-driver-and-online-seller',
    name: 'Raj Kumar',
    role: 'Rideshare Driver & Online Seller',
    company: 'Self-employed',
    industry: 'transport / e-commerce',
    tier: 'everyday',
    avatar: '/sales-negotiation/characters/cold-call-mastery/raj-kumar-rideshare-driver-and-online-seller.webp',
    difficulty: 'beginner',
    color: '#0369a1',
    age: 39,
    gender: 'male',
    ethnicity: 'Indian',
    tags: ['analytical', 'transport', 'hustler'],
    scenarioTitle: 'The Hustling Side-Gig Guy',
    scenarioDesc: "Raj is 39, Indian, sitting in his car between rides. He sells stuff online on the side and is always looking for ways to earn more. He's polite but time is money — he can't sit idle for long. Every minute on a useless call is a minute he's not making money.",
    goal: 'Book a 10-minute call for this evening.',
    category: 'cold-call-mastery',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 3000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 7000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "[notification ping] Hello, Raj speaking. Quick, I've got a surge coming in.",
      silence_1: "Brother, every minute on this call is $3 I'm not making. What's the ROI?",
      silence_2: "I need to see numbers before I agree to anything.",
      hangup: "[notification ping] That's my ride. Gotta go. Text me the details, I'll look tonight. *click*", 
    },
    coachingNotes: {
      whatWorks: "Talk to him like a fellow hustler. He understands ROI — speak his language. Time saved = money earned. Make that equation clear fast.",
      commonMistake: "Vague benefits like 'grow your business.' He wants specific numbers and he wants them in the first 30 seconds. Silence costs him money.",
      winningMove: "Say: 'Raj, quick question — how many hours a week do you spend managing your online seller account? Because most of my clients cut that by half and use the time for more rides.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 11. DR. ARTHUR PENDELTON — The Bureaucrat
  // ═════════════════════════════════════════════════════════════════
  'dr-arthur-pendelton-dean-of-admissions': {
    id: 'dr-arthur-pendelton-dean-of-admissions',
    name: 'Dr. Arthur Pendelton',
    role: 'Dean of Admissions',
    company: 'Northbridge Academy',
    industry: 'education',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/dr-arthur-pendelton-dean-of-admissions.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    age: 56,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['bureaucrat', 'education', 'smb'],
    scenarioTitle: 'The Bureaucrat',
    scenarioDesc: "Arthur hides behind committees and 'processes' to avoid taking sales calls. He's formal, clipped, and views unsolicited calls as a violation of protocol. He won't be rushed, but he also won't tolerate someone who doesn't respect his time.",
    goal: 'Find out who the actual decision maker is.',
    category: 'cold-call-mastery',
    personalityType: 'polite-but-firm',
    objectionStyle: 'deflects-to-email',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'silence_1' },
        { type: 'warn', delay: 12000, lineKey: 'silence_2' },
        { type: 'hangup', delay: 18000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Dr. Pendelton. All vendor inquiries must go through the procurement portal. State your business.",
      silence_1: "As I stated, there is a formal process for this. The committee reviews everything in the portal.",
      silence_2: "I cannot bypass committee regulations for a silent caller. That would be a violation of protocol.",
      hangup: "Please submit your proposal through the portal. Good day. *click*", 
    },
    coachingNotes: {
      whatWorks: "Acknowledge the process, but ask a question about his specific departmental goals to build rapport before submitting.",
      commonMistake: "Arguing that your product is too important for the portal. Or going silent — he interprets silence as disrespect.",
      winningMove: "Say: 'I'll happily use the portal. But before I fill out the 10-page form, is increasing early-enrollment actually a priority for your committee this semester?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 12. ZOE MITCHELL — The Deadline Driver (was Chloe Decker)
  // ═════════════════════════════════════════════════════════════════
  'zoe-mitchell-managing-editor': {
    id: 'zoe-mitchell-managing-editor',
    name: 'Zoe Mitchell',
    role: 'Managing Editor',
    company: 'Tribune Digital Network',
    industry: 'media',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/zoe-mitchell-managing-editor.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    age: 32,
    gender: 'female',
    ethnicity: 'White/American',
    tags: ['rushed', 'media', 'deadline'],
    scenarioTitle: 'The Deadline Driver',
    scenarioDesc: "Zoe is 30 minutes away from publishing a major piece and has zero patience. She's sharp, fast-talking, and treats every second like it costs money. She doesn't do small talk. She doesn't do silence. She does headlines and hard stops.",
    goal: 'Earn 2 more minutes of her time.',
    category: 'cold-call-mastery',
    personalityType: 'rushed',
    objectionStyle: 'no-time',
    silenceProfile: {
      pattern: 'instant',
      stages: [
        { type: 'hangup', delay: 5000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "[typing sounds] Zoe. I'm on deadline. You have thirty seconds.",
      silence_1: "Is this a pitch? Because I'm not taking pitches.",
      silence_2: "Okay, you're cutting into my final edit.",
      hangup: "Can't do this. Bye. *click*", 
    },
    coachingNotes: {
      whatWorks: "Be hyper-concise. Speak in headlines. Tell her exactly why you are relevant to her editorial workflow. You get 5 seconds of silence before she hangs up. Zero warnings.",
      commonMistake: "Using a slow, conversational tempo. It will frustrate her immediately. Any pause and she's gone.",
      winningMove: "Say: 'Not a pitch. I have a tool that cuts fact-checking time in half. Do you have 60 seconds or should I call after deadline?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 13. MARKUS THORNE — The Guarded Executive
  // ═════════════════════════════════════════════════════════════════
  'markus-thorne-vp-of-exploration': {
    id: 'markus-thorne-vp-of-exploration',
    name: 'Markus Thorne',
    role: 'VP of Exploration',
    company: 'Titan Energy Resources',
    industry: 'energy',
    companySize: 'startup',
    avatar: '/sales-negotiation/characters/cold-call-mastery/markus-thorne-vp-of-exploration.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    age: 48,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['guarded', 'energy', 'executive'],
    scenarioTitle: 'The Guarded Executive',
    scenarioDesc: "Markus doesn't trust outsiders and suspects every cold call is a competitor probing for info. He's terse, suspicious, and speaks in short bursts. He expects you to justify your existence in the first 5 seconds. Silence makes him assume you're gathering intel.",
    goal: 'Get him to agree to receive a one-page case study.',
    category: 'cold-call-mastery',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Thorne. Who told you to call me?",
      silence_1: "I don't discuss our operational metrics with strangers who can't speak.",
      silence_2: "Listen, I'm not giving you any data. Not over the phone.",
      hangup: "This conversation is over. *click*", 
    },
    coachingNotes: {
      whatWorks: "Focus entirely on industry trends and anonymous benchmarks, removing the pressure for him to share his own data.",
      commonMistake: "Asking him diagnostic questions about his current operations. Or pausing — he thinks you're fishing for info.",
      winningMove: "Say: 'No one told me to call. I'm calling because we just helped a firm in the Permian basin reduce downtime by 12%, and I thought you'd want to see the case study.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 14. DR. SUSAN LIN — The Silent Thinker
  // ═════════════════════════════════════════════════════════════════
  'dr-susan-lin-director-of-r-and-d': {
    id: 'dr-susan-lin-director-of-r-and-d',
    name: 'Dr. Susan Lin',
    role: 'Director of R&D',
    company: 'Novacell Therapeutics',
    industry: 'pharma',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/dr-susan-lin-director-of-r-and-d.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    age: 44,
    gender: 'female',
    ethnicity: 'Chinese-American',
    tags: ['analytical', 'pharma', 'silent'],
    scenarioTitle: 'The Silent Thinker',
    scenarioDesc: "Susan listens intensely but rarely speaks, creating awkward silences to test callers. She's a scientist — she wants data, not charm. She respects people who can handle silence and deliver substance. She gives you more time than most because she actually wants to hear what you have to say, but only if it's intelligent.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'analytical',
    objectionStyle: 'silent-treatment',
    silenceProfile: {
      pattern: 'patient',
      stages: [
        { type: 'warn', delay: 10000, lineKey: 'silence_1' },
        { type: 'warn', delay: 20000, lineKey: 'silence_2' },
        { type: 'warn', delay: 25000, lineKey: 'lastChance' },
        { type: 'hangup', delay: 30000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Dr. Lin. Go ahead.",
      silence_1: "[long pause] I'm listening. Is there a point?",
      silence_2: "You haven't said anything compelling yet.",
      lastChance: "I'll wait ten more seconds. If you have data, present it now.",
      hangup: "I have better things to do. *click*", 
    },
    coachingNotes: {
      whatWorks: "Ask open-ended, highly technical questions that force her to engage her expertise. She tolerates up to 30 seconds of silence if she thinks you're thinking.",
      commonMistake: "Rambling nervously to fill the silence. She respects silence more than filler.",
      winningMove: "Say: 'Dr. Lin, we've noticed a bottleneck in Phase II trial data aggregation. How are you currently managing unstructured clinical data?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 15. MARCUS REID — The Budget Defender (was Brian Dawkins)
  // ═════════════════════════════════════════════════════════════════
  'marcus-reid-claims-manager': {
    id: 'marcus-reid-claims-manager',
    name: 'Marcus Reid',
    role: 'Claims Manager',
    company: 'SafeGuard Assurance',
    industry: 'insurance',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/marcus-reid-claims-manager.webp',
    difficulty: 'beginner',
    color: '#0ea5e9',
    age: 46,
    gender: 'male',
    ethnicity: 'Black/American',
    tags: ['direct', 'insurance', 'budget'],
    scenarioTitle: 'The Budget Defender',
    scenarioDesc: "Marcus is obsessed with his departmental budget and shoots down anything that costs money. He's direct, no-nonsense, and has heard every budget justification in the book. He respects numbers and despises emotional appeals. If you pause, he assumes you don't have the data.",
    goal: 'Earn 2 more minutes of his time.',
    category: 'cold-call-mastery',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Marcus Reid. Whatever it is, I don't have budget for it this year.",
      silence_1: "I said no budget. What part of 'no' needs translation?",
      silence_2: "You're wasting your breath, my budget is locked until Q3.",
      hangup: "Call me next year. Bye. *click*", 
    },
    coachingNotes: {
      whatWorks: "Pivot immediately from 'cost' to 'savings'. Frame the conversation around recovering lost budget.",
      commonMistake: "Trying to argue that the product is worth finding budget for. Silence makes him think you're making up numbers.",
      winningMove: "Say: 'I'm not calling to spend your budget, I'm calling because we help claims managers find an average of 8% in operational savings. If I can't show you how, I'll hang up.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 16. KEVIN PETERSON — The Vendor Loyalist
  // ═════════════════════════════════════════════════════════════════
  'kevin-peterson-vp-of-supply-chain': {
    id: 'kevin-peterson-vp-of-supply-chain',
    name: 'Kevin Peterson',
    role: 'VP of Supply Chain',
    company: 'GlobalLink Logistics',
    industry: 'logistics',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/kevin-peterson-vp-of-supply-chain.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 51,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['loyal', 'logistics', 'enterprise'],
    scenarioTitle: 'The Vendor Loyalist',
    scenarioDesc: "Kevin has used the same routing software for a decade and hates change. He's polite but deeply entrenched. He views cold calls as an attack on his judgment. He'll listen briefly out of courtesy, but his patience is thin. Silence makes him think you don't understand logistics.",
    goal: 'Get him to admit one flaw in his current system.',
    category: 'cold-call-mastery',
    personalityType: 'polite-but-firm',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Kevin Peterson. We are fully locked in with our current provider and very happy.",
      silence_1: "I really don't have time to entertain switching systems today.",
      silence_2: "Look, migrating our routing data would be a nightmare. I'm not doing it.",
      hangup: "We're good. Thanks anyway. *click*", 
    },
    coachingNotes: {
      whatWorks: "Validate his loyalty, then ask a 'what if' question about a feature his legacy system likely lacks.",
      commonMistake: "Bashing his current vendor, which makes him defensive. Or pausing — he thinks you don't know the industry.",
      winningMove: "Say: 'I'd never ask you to rip out a system that works. But how are you handling real-time rerouting when ports get congested? Most legacy systems struggle there.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 17. CLAIRE DONOVAN — The Gatekeeper's Nightmare (was Rachel Zane)
  // ═════════════════════════════════════════════════════════════════
  'claire-donovan-head-of-production': {
    id: 'claire-donovan-head-of-production',
    name: 'Claire Donovan',
    role: 'Head of Production',
    company: 'Machina Precision',
    industry: 'manufacturing',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/claire-donovan-head-of-production.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 38,
    gender: 'female',
    ethnicity: 'White/American',
    tags: ['hostile', 'manufacturing', 'gatekeeper'],
    scenarioTitle: "The Gatekeeper's Nightmare",
    scenarioDesc: "Claire trained her gatekeeper to block everyone. You somehow slipped through, and she is furious about it. She's aggressive, time-starved, and views you as an intruder. She will hang up the moment you give her an excuse. Zero tolerance for hesitation.",
    goal: 'Earn 2 more minutes of her time.',
    category: 'cold-call-mastery',
    personalityType: 'hostile',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 3000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 6000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "How did you get past my assistant? I don't take unsolicited calls.",
      silence_1: "[starts counting on her watch] You have thirty seconds before I block your number.",
      silence_2: "You're rambling. Get to the point.",
      hangup: "I'm telling my assistant to blacklist you. Goodbye. *click*", 
    },
    coachingNotes: {
      whatWorks: "Extreme confidence and brevity. Own the interruption and hit her with a massive value prop.",
      commonMistake: "Apologizing for getting past the gatekeeper. Or hesitating — she sees it as weakness.",
      winningMove: "Say: 'I bypassed your assistant because reducing your machine downtime by 20% is a conversation for the Head of Production, not an admin.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 18. DAVID PARK — The Skeptical Techie (was Greg Halpert)
  // ═════════════════════════════════════════════════════════════════
  'david-park-cto': {
    id: 'david-park-cto',
    name: 'David Park',
    role: 'CTO',
    company: 'DataView Analytics',
    industry: 'SaaS',
    companySize: 'startup',
    avatar: '/sales-negotiation/characters/cold-call-mastery/david-park-cto.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 35,
    gender: 'male',
    ethnicity: 'Korean-American',
    tags: ['skeptical', 'saas', 'technical'],
    scenarioTitle: 'The Skeptical Techie',
    scenarioDesc: "David thinks most SaaS sales reps are technically illiterate and tries to trip them up. He's Korean-American, direct, and has built systems from scratch. He respects technical depth and despises buzzwords. Silence makes him think you don't understand your own product.",
    goal: 'Book a technical deep-dive call.',
    category: 'cold-call-mastery',
    personalityType: 'skeptical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 11000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "David Park. Look, if this is another AI wrapper pitch, save your breath.",
      silence_1: "What's under the hood? What's your p99 latency on write operations? Don't give me marketing fluff.",
      silence_2: "You clearly don't know your own stack.",
      hangup: "Have your engineers call me when you have a real product. *click*", 
    },
    coachingNotes: {
      whatWorks: "Speak his language. Drop one or two specific technical terms related to your integration or infrastructure.",
      commonMistake: "Sticking to high-level benefits. He views business benefits as 'fluff'. Silence = you don't know the tech.",
      winningMove: "Say: 'No AI wrappers here. It's a native integration built on Rust that handles data throughput at 10x the speed of standard REST APIs.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 19. HELEN TORRES — The Process Stickler (was Angela Martin)
  // ═════════════════════════════════════════════════════════════════
  'helen-torres-director-of-administration': {
    id: 'helen-torres-director-of-administration',
    name: 'Helen Torres',
    role: 'Director of Administration',
    company: 'Regional Care Hospital',
    industry: 'healthcare',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/helen-torres-director-of-administration.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 43,
    gender: 'female',
    ethnicity: 'Latina',
    tags: ['process', 'healthcare', 'protocol'],
    scenarioTitle: 'The Process Stickler',
    scenarioDesc: "Helen loves rules and forms. She hates cold calls because they subvert her processes. She's Latina, meticulous, and has built a career on compliance. She'll follow protocol to the letter, but she won't tolerate callers who don't respect the system. Silence makes her think you're avoiding protocol.",
    goal: 'Get her to agree to an introductory email.',
    category: 'cold-call-mastery',
    personalityType: 'gatekeeper-trained',
    objectionStyle: 'deflects-to-email',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'silence_1' },
        { type: 'warn', delay: 12000, lineKey: 'silence_2' },
        { type: 'hangup', delay: 18000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Helen Torres. We do not accept unsolicited vendor calls. Please send an email.",
      silence_1: "I am not deviating from hospital protocol. Send the email to procurement.",
      silence_2: "You are making this very difficult. I have patients to attend to.",
      hangup: "I am hanging up now. *click*",
    },
    coachingNotes: {
      whatWorks: "Align with her love of process. Show that you respect her authority.",
      commonMistake: "Trying to keep the conversation going when she has clearly stated the rule. Or pausing — she thinks you're evading protocol.",
      winningMove: "Say: 'I completely respect the protocol, Helen. What is the specific subject line I should use so you know it's the compliance software we discussed?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 20. BRADLEY CORBIN — The Sales Bro
  // ═════════════════════════════════════════════════════════════════
  'bradley-corbin-vp-of-sales': {
    id: 'bradley-corbin-vp-of-sales',
    name: 'Bradley Corbin',
    role: 'VP of Sales',
    company: 'NextGen FinTech',
    industry: 'finance',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/bradley-corbin-vp-of-sales.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 39,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['direct', 'finance', 'sales-bro'],
    scenarioTitle: 'The Sales Bro',
    scenarioDesc: "Bradley is a former cold-caller himself. He respects a good pitch but will brutally cut down a bad one. He's competitive, loud, and treats every call like a sparring match. He gives you time to perform because he remembers being in your seat, but he has no patience for amateurs.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'direct',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Bradley. I used to cold-call for a living. Let's see if you're better than I was.",
      silence_1: "Weak hook. Try again or I'm gone.",
      silence_2: "You're losing the deal, man. Bring it home.",
      hangup: "Not impressed. Keep practicing. *click*",
    },
    coachingNotes: {
      whatWorks: "Be hyper-confident and slightly self-deprecating. Use sales terminology to build peer rapport.",
      commonMistake: "Getting nervous and reverting to a robotic, script-reading tone. Or pausing — he thinks you're choking.",
      winningMove: "Say: 'I'll skip the warmup. You're probably bleeding 20% of your pipeline to bad follow-up. I have the fix. Do I have your attention?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 21. REBECCA COLE (was Samantha Jones)
  // ═════════════════════════════════════════════════════════════════
  'rebecca-cole-senior-buyer': {
    id: 'rebecca-cole-senior-buyer',
    name: 'Rebecca Cole',
    role: 'Senior Buyer',
    company: 'Chic Fashion Group',
    industry: 'retail',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/rebecca-cole-senior-buyer.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 30,
    gender: 'female',
    ethnicity: 'White/American',
    tags: ['rushed', 'retail', 'traveling'],
    scenarioTitle: 'The Rushed Buyer',
    scenarioDesc: "Rebecca is constantly traveling between fashion shows and supplier meetings. She has zero downtime. She's walking through an airport terminal right now, rolling a carry-on, and answered because she thought it was her connecting flight. She will hang up mid-sentence if you waste a syllable.",
    goal: 'Earn 2 more minutes of her time.',
    category: 'cold-call-mastery',
    personalityType: 'rushed',
    objectionStyle: 'no-time',
    silenceProfile: {
      pattern: 'instant',
      stages: [
        { type: 'hangup', delay: 5000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "[airport announcement in background] Rebecca. I'm walking into a terminal, I have literally one minute.",
      silence_1: "[PA system: boarding group one] Boarding group one just called, what is it?",
      silence_2: "[airplane sounds getting louder] I'm handing them my ticket, wrap it up.",
      hangup: "[muffled by PA system] Final boarding call... sorry, I have to — *click*", 
    },
    coachingNotes: {
      whatWorks: "Acknowledge the immediate time constraint and ask to schedule for when she lands. You get 5 seconds of silence before she hangs up — no warnings.",
      commonMistake: "Trying to squeeze a full pitch into her chaotic environment. Any pause and she's gone.",
      winningMove: "Say: 'Have a great flight. Can I grab 5 minutes on Thursday when you're back at your desk to talk about supplier consolidation?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 22. MIKE DELFINO (was Bill Lumbergh)
  // ═════════════════════════════════════════════════════════════════
  'mike-delfino-site-manager': {
    id: 'mike-delfino-site-manager',
    name: 'Mike Delfino',
    role: 'Site Manager',
    company: 'Apex Builders',
    industry: 'construction',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/mike-delfino-site-manager.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 48,
    gender: 'male',
    ethnicity: 'Italian-American',
    tags: ['distracted', 'construction', 'indifferent'],
    scenarioTitle: 'The Unbothered Manager',
    scenarioDesc: "Mike is on a loud construction site. He is calm, unhurried, but completely indifferent to you. He's 48, Italian-American, and has seen every vendor pitch under the sun. He won't hang up quickly — he'll just let you talk into the void until you realize he stopped listening. Silence doesn't faze him; he uses it as a weapon.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'distracted',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Yeah, Mike here. [loud drilling noise] Yeah, I'm just not interested right now.",
      silence_1: "[quieter] Look, we're doing fine. Don't really need anything.",
      silence_2: "[even quieter] I've gotta go check on the concrete pour.",
      hangup: "[quiet, calm] Yeah, I'm gonna let you go. *click*", 
    },
    coachingNotes: {
      whatWorks: "Disrupt his apathy with a highly specific, high-risk problem that sites like his face. He gives you 12 seconds of silence before hanging up.",
      commonMistake: "Accepting his mild 'not interested' and giving up easily.",
      winningMove: "Say: 'I get it, you're pouring concrete. But how are you handling the new OSHA silica compliance rules that kick in next month?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 23. VICTOR KANE (was Harvey Specter)
  // ═════════════════════════════════════════════════════════════════
  'victor-kane-senior-partner': {
    id: 'victor-kane-senior-partner',
    name: 'Victor Kane',
    role: 'Senior Partner',
    company: 'Sterling & Associates',
    industry: 'legal',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/victor-kane-senior-partner.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 52,
    gender: 'male',
    ethnicity: 'British',
    tags: ['ego', 'legal', 'partner'],
    scenarioTitle: 'The Ego Driven Partner',
    scenarioDesc: "Victor thinks he is the smartest person in the room and resents being 'sold' to. He's 52, British, and has billed $2,000 an hour for 20 years. He views cold calls as a waste of his intellectual bandwidth. He will test you with silence just to see if you crack.",
    goal: 'Get him to admit one flaw in his current system.',
    category: 'cold-call-mastery',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 4000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 8000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Victor Kane. I use the best firms for everything, so you're probably wasting my time.",
      silence_1: "You haven't told me anything my current guys don't do.",
      silence_2: "I can see this is your first day.",
      hangup: "Stick to the minor leagues. *click*",
    },
    coachingNotes: {
      whatWorks: "Challenge his ego. Imply that his 'best firms' might actually be outdated or missing a key advantage.",
      commonMistake: "Agreeing with him and acting overly submissive. Or hesitating — he interprets silence as insecurity.",
      winningMove: "Say: 'If you're using LexisNexis, you're using what everyone else uses. I'm calling about an edge your competitors don't have yet.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 24. DIANA MARSH (was Olivia Pope)
  // ═════════════════════════════════════════════════════════════════
  'diana-marsh-managing-director': {
    id: 'diana-marsh-managing-director',
    name: 'Diana Marsh',
    role: 'Managing Director',
    company: 'Crisis Management Partners',
    industry: 'consulting',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/diana-marsh-managing-director.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 45,
    gender: 'female',
    ethnicity: 'Black/American',
    tags: ['direct', 'consulting', 'fixer'],
    scenarioTitle: 'The Fixer',
    scenarioDesc: "Diana handles massive corporate crises. She only cares about results and detests small talk. She's 45, Black, and has built a reputation on fixing disasters in 48 hours. She respects speed and precision. Silence makes her think you don't have a solution.",
    goal: 'Earn 2 more minutes of her time.',
    category: 'cold-call-mastery',
    personalityType: 'direct',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Diana Marsh. State your business.",
      silence_1: "That's a claim, not proof. Back it up.",
      silence_2: "[deliberate silence — she is waiting for your evidence]",
      hangup: "It's handled. Don't call back."
    },
    coachingNotes: {
      whatWorks: "Lead with a powerful, undeniable statistic or case study result immediately.",
      commonMistake: "Asking 'how are you today?' or trying to build rapport through chit-chat. Silence = no solution.",
      winningMove: "Say: 'I help consulting firms reduce client response times by 40% using automated triage. I have the data to prove it.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 25. JAMES ORTIZ (was Tom Haverford)
  // ═════════════════════════════════════════════════════════════════
  'james-ortiz-property-manager': {
    id: 'james-ortiz-property-manager',
    name: 'James Ortiz',
    role: 'Property Manager',
    company: 'Elite Urban Living',
    industry: 'real-estate',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/james-ortiz-property-manager.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 35,
    gender: 'male',
    ethnicity: 'Mexican-American',
    tags: ['price-first', 'real-estate', 'analytical'],
    scenarioTitle: 'The Price Shopper',
    scenarioDesc: "James is always looking for a deal. He won't talk value until he knows the price. He's 35, Mexican-American, manages 200 units, and treats every vendor call like a negotiation. He respects transparency and despises people who dodge the numbers. He'll give you time if you're straight with him.",
    goal: 'Get him to agree to receive a one-page case study.',
    category: 'cold-call-mastery',
    personalityType: 'analytical',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'silence_1' },
        { type: 'warn', delay: 12000, lineKey: 'silence_2' },
        { type: 'hangup', delay: 18000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "James here. Look, man, just tell me how much it costs upfront.",
      silence_1: "Stop dodging. Give me a ballpark figure.",
      silence_2: "Okay, let's say I'm interested. What's your best price? Not the list price — your real number.",
      hangup: "Too expensive. Catch you later. *click*", 
    },
    coachingNotes: {
      whatWorks: "Give a realistic, wide range to satisfy his request, then immediately pivot to the value that justifies it.",
      commonMistake: "Refusing to give any price at all, which breaks trust. Silence makes him think you're hiding fees.",
      winningMove: "Say: 'It ranges from $500 to $2000 a month depending on units. But before we talk price, are you actually losing tenants due to slow maintenance?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 26. DR. ALAN FOSTER (was Dr. Richard Webber)
  // ═════════════════════════════════════════════════════════════════
  'dr-alan-foster-provost': {
    id: 'dr-alan-foster-provost',
    name: 'Dr. Alan Foster',
    role: 'Provost',
    company: 'State University System',
    industry: 'education',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/dr-alan-foster-provost.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 60,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['silent', 'education', 'authority'],
    scenarioTitle: 'The Silent Authority',
    scenarioDesc: "Dr. Foster uses silence as a weapon to make salespeople uncomfortable and reveal their desperation. He's 60, soft-spoken, and has a PhD in organizational psychology. He knows exactly what you're doing and he's waiting to see if you'll panic. He respects people who can handle silence.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'analytical',
    objectionStyle: 'silent-treatment',
    silenceProfile: {
      pattern: 'patient',
      stages: [
        { type: 'warn', delay: 10000, lineKey: 'silence_1' },
        { type: 'warn', delay: 20000, lineKey: 'silence_2' },
        { type: 'warn', delay: 25000, lineKey: 'lastChance' },
        { type: 'hangup', delay: 30000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Dr. Foster. ...I'm listening.",
      silence_1: "...Is that it?",
      silence_2: "...I remain unconvinced.",
      lastChance: "...One more sentence. Make it count.",
      hangup: "We are done. Goodbye."
    },
    coachingNotes: {
      whatWorks: "Match his silence. Ask a strong question and then completely stop talking until he answers. He tolerates up to 30 seconds.",
      commonMistake: "Talking over his silence and negotiating against yourself.",
      winningMove: "Ask: 'Dr. Foster, how is your faculty handling the new LMS transition?' Then, do not say a single word until he speaks."
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 27. NATALIE CROSS (was Diana Christensen)
  // ═════════════════════════════════════════════════════════════════
  'natalie-cross-director-of-broadcasting': {
    id: 'natalie-cross-director-of-broadcasting',
    name: 'Natalie Cross',
    role: 'Director of Broadcasting',
    company: 'National Broadcasting Corp',
    industry: 'media',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/natalie-cross-director-of-broadcasting.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 44,
    gender: 'female',
    ethnicity: 'White/American',
    tags: ['cynical', 'media', 'executive'],
    scenarioTitle: 'The Cynical Executive',
    scenarioDesc: "Natalie has heard every pitch and thinks 99% of vendors are peddling snake oil. She's 44, sharp-tongued, and has fired three ad agencies this year. She doesn't do courtesy. She does results. If you pause, she assumes you don't believe your own pitch.",
    goal: 'Earn 2 more minutes of her time.',
    category: 'cold-call-mastery',
    personalityType: 'skeptical',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'instant',
      stages: [
        { type: 'hangup', delay: 6000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Natalie. I give you a 1% chance of telling me something I don't already know.",
      silence_1: "Heard it. Next.",
      silence_2: "You're boring me. Give me something real or I'm gone.",
      hangup: "Told you. Boring. *click*",
    },
    coachingNotes: {
      whatWorks: "Acknowledge her cynicism and offer a contrarian insight about her industry that she can't easily dismiss.",
      commonMistake: "Trying to impress her with standard feature-benefit lists. Silence = you have nothing original.",
      winningMove: "Say: 'You probably know that cord-cutting is up 15%. What you don't know is how your competitors are monetizing the churn using our analytics.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 28. ROBERT VANCE (was John Hammond)
  // ═════════════════════════════════════════════════════════════════
  'robert-vance-head-of-operations': {
    id: 'robert-vance-head-of-operations',
    name: 'Robert Vance',
    role: 'Head of Operations',
    company: 'InGen Energy',
    industry: 'energy',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/robert-vance-head-of-operations.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 50,
    gender: 'male',
    ethnicity: 'White/Texan',
    tags: ['email-dodger', 'energy', 'soft-reject'],
    scenarioTitle: 'The Email Dodger',
    scenarioDesc: "Robert uses 'send me an email' as a polite way to end calls, but he never actually reads them. He's 50, Texan, and has perfected the art of the soft rejection. He won't be rude, but he won't engage either. Silence makes him think you're not confident enough to push back.",
    goal: 'Get him to commit to a 10-minute call tomorrow.',
    category: 'cold-call-mastery',
    personalityType: 'polite-but-firm',
    objectionStyle: 'deflects-to-email',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Robert here. Sounds interesting, why don't you just shoot me an email with some info?",
      silence_1: "I said I'd look at an email, that's the best I can do.",
      silence_2: "You can send it to the general inbox. It's info@ingenenergy.com. [knowing it goes to spam]",
      hangup: "Just email it. Goodbye. *click*", 
    },
    coachingNotes: {
      whatWorks: "Call out the 'email dodge' playfully and ask for real engagement.",
      commonMistake: "Happily agreeing to send the email and ending the call, losing the prospect forever.",
      winningMove: "Say: 'Robert, usually when someone says that, it means they're never going to read it. Should I actually send it, or is this just a polite no?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 29. DR. ELENA VOSS (was Meredith Grey)
  // ═════════════════════════════════════════════════════════════════
  'dr-elena-voss-vp-of-clinical-trials': {
    id: 'dr-elena-voss-vp-of-clinical-trials',
    name: 'Dr. Elena Voss',
    role: 'VP of Clinical Trials',
    company: 'Seattle Grace Pharma',
    industry: 'pharma',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/dr-elena-voss-vp-of-clinical-trials.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 39,
    gender: 'female',
    ethnicity: 'German',
    tags: ['rushed', 'pharma', 'fda'],
    scenarioTitle: 'The Busy Executive',
    scenarioDesc: "Dr. Voss is constantly putting out fires and has zero tolerance for fluff. She's 39, German, and has an FDA audit in 20 minutes. She answered because she thought it was her regulatory counsel. She will hang up mid-word if you're not relevant. Instantly.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'rushed',
    objectionStyle: 'no-time',
    silenceProfile: {
      pattern: 'instant',
      stages: [
        { type: 'hangup', delay: 4000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Dr. Voss. I have an FDA audit in 20 minutes, what do you want?",
      silence_1: "Get to the point, my timer is ticking.",
      silence_2: "I don't have time for a presentation right now. The FDA does not wait.",
      hangup: "I have to go. *click*",
    },
    coachingNotes: {
      whatWorks: "Mirror her urgency. Speak quickly and deliver the value prop in under 15 seconds. You get 4 seconds of silence before she hangs up.",
      commonMistake: "Ignoring her stated time constraint and talking slowly.",
      winningMove: "Say: 'Good luck with the FDA. I'll be quick: we automate trial compliance reporting. Can we talk next week when the audit is over?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 30. CARL HENDRICKS (was Stan Beeman)
  // ═════════════════════════════════════════════════════════════════
  'carl-hendricks-director-of-risk-management': {
    id: 'carl-hendricks-director-of-risk-management',
    name: 'Carl Hendricks',
    role: 'Director of Risk Management',
    company: 'Shield Mutual',
    industry: 'insurance',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/carl-hendricks-director-of-risk-management.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 55,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['risk-averse', 'insurance', 'cautious'],
    scenarioTitle: 'The Risk Averse Manager',
    scenarioDesc: "Carl's entire job is avoiding risk. Buying new software is a massive risk to him. He's 55, methodical, and has a spreadsheet for every decision. He won't be rushed, but he won't tolerate vague answers either. Silence makes him think you haven't done your risk analysis.",
    goal: 'Get him to agree to receive a one-page case study.',
    category: 'cold-call-mastery',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'silence_1' },
        { type: 'warn', delay: 14000, lineKey: 'silence_2' },
        { type: 'warn', delay: 18000, lineKey: 'lastChance' },
        { type: 'hangup', delay: 22000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Carl here. We don't like switching vendors, it causes too much disruption.",
      silence_1: "Implementation is always a nightmare. Not interested.",
      silence_2: "The risk profile of changing systems is too high for us right now. Our actuarial tables say no.",
      hangup: "We're sticking with what works. Bye. *click*", 
    },
    coachingNotes: {
      whatWorks: "Focus heavily on security, seamless integration, and zero-downtime implementation.",
      commonMistake: "Pitching innovation and disruption, which terrifies him. Silence = you haven't mitigated risk.",
      winningMove: "Say: 'I understand. What if I could show you a case study where a firm implemented our system in parallel with zero downtime and no risk to current operations?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 31. CYNTHIA WALLACE
  // ═════════════════════════════════════════════════════════════════
  'cynthia-wallace-vp-of-logistics': {
    id: 'cynthia-wallace-vp-of-logistics',
    name: 'Cynthia Wallace',
    role: 'VP of Logistics',
    company: 'TransGlobal Freight',
    industry: 'logistics',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/cynthia-wallace-vp-of-logistics.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 43,
    gender: 'female',
    ethnicity: 'White/American',
    tags: ['distracted', 'logistics', 'crisis'],
    scenarioTitle: 'The Distracted VP',
    scenarioDesc: "Cynthia is dealing with a driver strike and is barely listening to you. She's 43, on her third cup of coffee, and has the union rep on the other line. She answered by mistake. She has no bandwidth for you. Zero. Silence makes her remember she has a crisis to handle.",
    goal: 'Earn 2 more minutes of her time.',
    category: 'cold-call-mastery',
    personalityType: 'distracted',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'instant',
      stages: [
        { type: 'hangup', delay: 5000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Yeah, Cynthia. Hold on... tell dispatch to hold the trucks. Okay, what do you want?",
      silence_1: "Look, I'm dealing with a crisis right now. The union is on the line.",
      silence_2: "You're not helping me solve this strike, so I need to go.",
      hangup: "Can't talk. *click*",
    },
    coachingNotes: {
      whatWorks: "Acknowledge the crisis. If your product helps, tie it in. If not, politely back out and ask to call next week. You get 5 seconds of silence.",
      commonMistake: "Ignoring her strike comment and pushing your standard pitch.",
      winningMove: "Say: 'Sounds like you have a major fire to put out. I'll get off the phone. Can I call you next Tuesday when things settle down?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 32. TONY MARCHETTI (was Frank Sobotka)
  // ═════════════════════════════════════════════════════════════════
  'tony-marchetti-plant-director': {
    id: 'tony-marchetti-plant-director',
    name: 'Tony Marchetti',
    role: 'Plant Director',
    company: 'Harbor Industrial',
    industry: 'manufacturing',
    companySize: 'startup',
    avatar: '/sales-negotiation/characters/cold-call-mastery/tony-marchetti-plant-director.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 58,
    gender: 'male',
    ethnicity: 'Italian',
    tags: ['hostile', 'manufacturing', 'old-school'],
    scenarioTitle: 'The Old School Director',
    scenarioDesc: "Tony hates new technology and thinks software companies are scamming honest businesses. He's 58, Italian, and has run this plant for 30 years with a clipboard and a gut feeling. He respects blunt honesty and despises smooth talkers. Silence makes him think you're calculating how to rip him off.",
    goal: 'Get him to admit one flaw in his current system.',
    category: 'cold-call-mastery',
    personalityType: 'hostile',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 4000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 8000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Tony. How much is this gonna cost me? Because I'm not buying.",
      silence_1: "I asked you a question. Give me a number.",
      silence_2: "All you software guys just want to bleed us dry. I've seen this before.",
      hangup: "Take me off your list. *click*", 
    },
    coachingNotes: {
      whatWorks: "Be blunt back. Tell him you aren't trying to sell him anything today, just seeing if it even makes sense.",
      commonMistake: "Trying to justify the ROI before he even understands the product. Silence = you're hiding something.",
      winningMove: "Say: 'Tony, I don't know if it costs you anything yet because I don't even know if your plant can use it. Are you guys still tracking downtime on whiteboards?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 33. DEREK HOLLOWAY (was Erlich Bachman)
  // ═════════════════════════════════════════════════════════════════
  'derek-holloway-ceo': {
    id: 'derek-holloway-ceo',
    name: 'Derek Holloway',
    role: 'CEO',
    company: 'Pied Piper Systems',
    industry: 'SaaS',
    companySize: 'startup',
    avatar: '/sales-negotiation/characters/cold-call-mastery/derek-holloway-ceo.webp',
    difficulty: 'intermediate',
    color: '#f59e0b',
    age: 31,
    gender: 'male',
    ethnicity: 'British',
    tags: ['arrogant', 'saas', 'founder'],
    scenarioTitle: 'The Arrogant Founder',
    scenarioDesc: "Derek thinks his startup is the next big thing and considers most sales calls beneath him. He's 31, British, and has already raised two rounds. He gives you exactly 30 seconds because he read somewhere that's how long a pitch should be. He will cut you off at 31. Silence makes him think you're not worth his time.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'gatekeeper-trained',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 3000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 6000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Derek Holloway. You have exactly thirty seconds to impress me.",
      silence_1: "That's fifteen seconds down, and I'm bored.",
      silence_2: "You clearly don't understand hyper-growth scaling or burn rate.",
      hangup: "Next. *click*",
    },
    coachingNotes: {
      whatWorks: "Pander slightly to his ego, then hit him with a pain point that only scaling founders face.",
      commonMistake: "Pitching basic features. He only cares about 'scale' and 'valuation'. Silence = you're not in his league.",
      winningMove: "Say: 'Most founders I talk to are bleeding cash on customer acquisition as they scale. What's your CAC payback period right now?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 34. PATRICIA MORALES
  // ═════════════════════════════════════════════════════════════════
  'patricia-morales-cfo': {
    id: 'patricia-morales-cfo',
    name: 'Patricia Morales',
    role: 'CFO',
    company: 'Apex Capital Partners',
    industry: 'finance',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/patricia-morales-cfo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 47,
    gender: 'female',
    ethnicity: 'Colombian-American',
    tags: ['gatekeeper', 'finance', 'deflector'],
    scenarioTitle: 'The Gatekeeper Graduate',
    scenarioDesc: "Patricia has sat through 200 cold calls in her career. She knows every trick in the book and has a standard process for ending them in under 30 seconds. She's 47, Colombian-American, and has a black belt in deflection. She won't be charmed. She won't be pressured. She will hang up the moment you hesitate.",
    goal: 'Get her to agree to receive a one-page ROI summary by end of day.',
    category: 'cold-call-mastery',
    personalityType: 'gatekeeper-trained',
    objectionStyle: 'deflects-to-email',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Patricia Morales. And before you start — just send me an email.",
      silence_1: "[puts you on hold for 10 seconds] Sorry, that was actually important. Where were you?",
      silence_2: "This is exactly why I screen my calls.",
      hangup: "We're done here. Don't call this number again. *click*", 
    },
    coachingNotes: {
      whatWorks: "Acknowledge her email preference, then make one specific claim that's too intriguing to dismiss without a 90-second explanation.",
      commonMistake: "Arguing against the email — it signals you're not listening, which confirms her instinct to hang up.",
      winningMove: "Say: 'I'll send it right now while we're on the phone — but I need one number from you to make it relevant. What's your current cost of capital?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 35. DR. MARCUS WEBB (was Gregory House)
  // ═════════════════════════════════════════════════════════════════
  'dr-marcus-webb-vp-of-procurement': {
    id: 'dr-marcus-webb-vp-of-procurement',
    name: 'Dr. Marcus Webb',
    role: 'VP of Procurement',
    company: 'Princeton Plainsboro Health',
    industry: 'healthcare',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/dr-marcus-webb-vp-of-procurement.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 45,
    gender: 'male',
    ethnicity: 'British-Indian',
    tags: ['hostile', 'healthcare', 'analyst'],
    scenarioTitle: 'The Brutal Analyst',
    scenarioDesc: "Dr. Webb is brutally blunt, highly analytical, and views salespeople as a waste of oxygen. He's 45, British-Indian, and has a PhD in operations research. He will destroy you with data if you give him the chance. He respects only one thing: hard numbers delivered fast. Silence makes him think you're making things up.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'hostile',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Webb. Give me one statistical reason I shouldn't hang up.",
      silence_1: "That's an anecdote, not data. Try again.",
      silence_2: "According to the last McKinsey report, procurement waste is 6.2%. You said 18.4%. Which study are you citing?",
      hangup: "You bore me. Goodbye."
    },
    coachingNotes: {
      whatWorks: "Throw hard, verifiable statistics at him immediately. Don't ask how his day is.",
      commonMistake: "Trying to build rapport or using soft language like 'we feel' or 'we hope'. Silence = no data.",
      winningMove: "Say: 'Hospitals using our procurement matrix reduce supply chain waste by 18.4% within 6 months. I want to show you the methodology.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 36. PETE DONOVAN (was Glenn Sturgis)
  // ═════════════════════════════════════════════════════════════════
  'pete-donovan-director-of-store-operations': {
    id: 'pete-donovan-director-of-store-operations',
    name: 'Pete Donovan',
    role: 'Director of Store Operations',
    company: 'Cloud9 Superstores',
    industry: 'retail',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/pete-donovan-director-of-store-operations.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 50,
    gender: 'male',
    ethnicity: 'Irish-American',
    tags: ['loyal', 'retail', 'veteran'],
    scenarioTitle: 'The Loyal Lifer',
    scenarioDesc: "Pete has been with the company for 20 years and has deeply entrenched relationships with existing vendors. He's 50, Irish-American, and views any new vendor as a threat to his network. He won't be rushed, but he won't tolerate disrespect either. Silence makes him think you don't value loyalty.",
    goal: 'Get him to admit one flaw in his current system.',
    category: 'cold-call-mastery',
    personalityType: 'polite-but-firm',
    objectionStyle: 'vendor-loyal',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 7000, lineKey: 'silence_1' },
        { type: 'warn', delay: 14000, lineKey: 'silence_2' },
        { type: 'warn', delay: 18000, lineKey: 'lastChance' },
        { type: 'hangup', delay: 22000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Pete here. Just so you know, we've used the same POS vendor since 2005 and they're like family.",
      silence_1: "I really don't want to betray our current partners.",
      silence_2: "I'm just not comfortable making a change.",
      lastChance: "Alright, one last chance. I've been fair with you. Show me something that doesn't make me feel like I'm turning my back on family.",
      hangup: "We're happy where we are. Take care. *click*", 
    },
    coachingNotes: {
      whatWorks: "Reframe your solution as an 'addition' or 'compliment' to his current vendor, not a replacement.",
      commonMistake: "Trying to convince him his current vendor is outdated.",
      winningMove: "Say: 'I would never ask you to replace a 20-year relationship. We actually sit on top of your current POS to handle the online-order integrations they don't do.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 37. VICTORIA ASHFORD (was Lucille Bluth)
  // ═════════════════════════════════════════════════════════════════
  'victoria-ashford-vp-of-development': {
    id: 'victoria-ashford-vp-of-development',
    name: 'Victoria Ashford',
    role: 'VP of Development',
    company: 'Bluth Company',
    industry: 'construction',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/victoria-ashford-vp-of-development.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 52,
    gender: 'female',
    ethnicity: 'British',
    tags: ['dismissive', 'construction', 'elite'],
    scenarioTitle: 'The Dismissive Executive',
    scenarioDesc: "Victoria is highly dismissive of anyone she perceives as 'below' her, which is almost everyone. She's 52, upper-class British, and has never taken a cold call in her life. Her assistant usually screens them. She will hang up the moment she realizes you're selling something. No hesitation.",
    goal: 'Earn 2 more minutes of her time.',
    category: 'cold-call-mastery',
    personalityType: 'hostile',
    objectionStyle: 'brush-off',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 3000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 6000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Victoria. I don't know who let you through, but I don't speak to salespeople.",
      silence_1: "Are you still on the line? How tedious.",
      silence_2: "I'm looking for my assistant to have you disconnected.",
      hangup: "Jennifer? Can you please block this number? Thank you. *click*", 
    },
    coachingNotes: {
      whatWorks: "Leverage a competitor's name or a high-status reference to force her to pay attention out of FOMO.",
      commonMistake: "Trying to be friendly or accommodating. She respects power. Silence = you're beneath her.",
      winningMove: "Say: 'I'm calling because your competitors at Sitwell Construction just used our software to cut permitting delays in half, and I figured you wouldn't want them having the edge.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 38. EDDIE MORRA (was Saul Goodman)
  // ═════════════════════════════════════════════════════════════════
  'eddie-morra-managing-partner': {
    id: 'eddie-morra-managing-partner',
    name: 'Eddie Morra',
    role: 'Managing Partner',
    company: 'Goodman & Associates',
    industry: 'legal',
    companySize: 'smb',
    avatar: '/sales-negotiation/characters/cold-call-mastery/eddie-morra-managing-partner.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 48,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['slippery', 'legal', 'talker'],
    scenarioTitle: 'The Slippery Negotiator',
    scenarioDesc: "Eddie is a master talker who will spin the conversation in circles until you give up. He's 48, fast-talking, and has talked his way out of every contract dispute in the city. He respects people who can keep up with his pace. Silence makes him think he's won.",
    goal: 'Get him to commit to a specific next step.',
    category: 'cold-call-mastery',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Eddie here. Hey, love the hustle, but we are absolutely airtight right now, amigo.",
      silence_1: "Like I said, we're swimming in clients, don't need the help.",
      silence_2: "You're persistent, I'll give you that, but the wallet is closed. Court's in session.",
      hangup: "Better luck on the next one, kid. *click*",
    },
    coachingNotes: {
      whatWorks: "Cut through his charm with a hard, unarguable fact about his operational inefficiency.",
      commonMistake: "Getting caught up in his banter and losing control of the call. Or pausing — he fills the gap and takes over.",
      winningMove: "Say: 'I know you have clients, Eddie. But I also know your paralegals are spending 10 hours a week on manual discovery formatting. Want that billable time back?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 39. TREVOR BLACKWELL (was Kendall Roy)
  // ═════════════════════════════════════════════════════════════════
  'trevor-blackwell-partner': {
    id: 'trevor-blackwell-partner',
    name: 'Trevor Blackwell',
    role: 'Partner',
    company: 'Waystar Strategy',
    industry: 'consulting',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/trevor-blackwell-partner.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 38,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['buzzword', 'consulting', 'jargon'],
    scenarioTitle: 'The Buzzword Executive',
    scenarioDesc: "Trevor speaks entirely in corporate jargon and demands absolute synergy before considering a deal. He's 38, MBA from Wharton, and has never had an original thought. He will judge you based on how many buzzwords you can fit into a sentence. Silence makes him think you're not 'strategic' enough.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'skeptical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 5000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 10000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Trevor. How does this align with our core paradigm shift?",
      silence_1: "I need to see the dynamic synergy here, man.",
      silence_2: "You're not giving me the optics I need to take this upstream to the board.",
      hangup: "Yeah, not a fit for our ecosystem. Bye. *click*", 
    },
    coachingNotes: {
      whatWorks: "Translate his buzzwords into actual business value. Ground him with real numbers.",
      commonMistake: "Trying to out-jargon him. It makes you sound fake. Silence = you're not strategic.",
      winningMove: "Say: 'The synergy is simple: this tool cuts your deliverable turnaround time by 2 days, increasing your margins per project by 5%.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 40. LINDA PARK (was Barbara Corcoran)
  // ═════════════════════════════════════════════════════════════════
  'linda-park-director-of-acquisitions': {
    id: 'linda-park-director-of-acquisitions',
    name: 'Linda Park',
    role: 'Director of Acquisitions',
    company: 'Pinnacle Real Estate',
    industry: 'real-estate',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/linda-park-director-of-acquisitions.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 42,
    gender: 'female',
    ethnicity: 'Korean-American',
    tags: ['price-first', 'real-estate', 'roi'],
    scenarioTitle: 'The Bottom-Line Director',
    scenarioDesc: "Linda is intensely focused on price and ROI. If she can't see the money immediately, she's out. She's 42, Korean-American, and has closed $400M in deals. She doesn't do feelings. She does spreadsheets. She will hang up at the first pause because she assumes you don't have the numbers.",
    goal: 'Get her to agree to receive a one-page ROI summary.',
    category: 'cold-call-mastery',
    personalityType: 'direct',
    objectionStyle: 'price-first',
    silenceProfile: {
      pattern: 'instant',
      stages: [
        { type: 'hangup', delay: 5000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Linda. How much does it cost and how much will it make me?",
      silence_1: "Numbers. I just want the numbers. No stories.",
      silence_2: "If you don't have a clear ROI, we're done talking.",
      hangup: "Too rich for my blood. I'm out. *click*", 
    },
    coachingNotes: {
      whatWorks: "Give her the exact ROI equation immediately. Be crisp and confident.",
      commonMistake: "Hiding the price until the end of the presentation. Silence = you don't know the math.",
      winningMove: "Say: 'It costs $2k a month, and it generally pays for itself on the first acquisition deal you close faster. Want to see the math?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 41. DR. NEIL PATEL (was Dr. Sheldon Cooper)
  // ═════════════════════════════════════════════════════════════════
  'dr-neil-patel-chancellor': {
    id: 'dr-neil-patel-chancellor',
    name: 'Dr. Neil Patel',
    role: 'Chancellor',
    company: 'Tech University',
    industry: 'education',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/dr-neil-patel-chancellor.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 55,
    gender: 'male',
    ethnicity: 'Indian-American',
    tags: ['pedantic', 'education', 'academic'],
    scenarioTitle: 'The Pedantic Academic',
    scenarioDesc: "Dr. Patel will pick apart your language, statistics, and logic rather than engaging with the actual product. He's 55, Indian-American, and has a Nobel nomination in economics. He respects intellectual rigor and despises sloppy thinking. He gives you time because he enjoys the debate, but only if you're sharp.",
    goal: 'Earn 2 more minutes of his time.',
    category: 'cold-call-mastery',
    personalityType: 'analytical',
    objectionStyle: 'silent-treatment',
    silenceProfile: {
      pattern: 'patient',
      stages: [
        { type: 'warn', delay: 10000, lineKey: 'silence_1' },
        { type: 'warn', delay: 20000, lineKey: 'silence_2' },
        { type: 'warn', delay: 25000, lineKey: 'lastChance' },
        { type: 'hangup', delay: 30000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Dr. Patel. Your introductory premise is statistically improbable.",
      silence_1: "You are employing a logical fallacy. Proceed, but with caution.",
      silence_2: "The empirical data does not support your claims. Your sample size is suspect.",
      hangup: "This conversation lacks intellectual rigor. Goodbye."
    },
    coachingNotes: {
      whatWorks: "Challenge him with a complex, highly specific academic operational problem that forces him to engage his intellect. He tolerates up to 30 seconds of silence.",
      commonMistake: "Using generic sales phrases like 'game-changer' or 'synergy'.",
      winningMove: "Say: 'Statistically improbable, perhaps. But how are you accounting for the 15% variance in grant-funding allocation reporting across departments?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 42. JACK STERLING (was Don Draper)
  // ═════════════════════════════════════════════════════════════════
  'jack-sterling-vp-of-programming': {
    id: 'jack-sterling-vp-of-programming',
    name: 'Jack Sterling',
    role: 'VP of Programming',
    company: 'Sterling Cooper Media',
    industry: 'media',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/jack-sterling-vp-of-programming.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 47,
    gender: 'male',
    ethnicity: 'White/American',
    tags: ['visionary', 'media', 'creative'],
    scenarioTitle: 'The Unimpressed Visionary',
    scenarioDesc: "Jack relies on intuition and hates data-driven software pitches. He cares about the 'story' and nothing else. He's 47, creative director at heart, and has greenlit three Emmy winners. He will hang up the moment you mention 'analytics' or 'optimization.' He respects visionaries and despises technicians.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'distracted',
    objectionStyle: 'not-interested',
    silenceProfile: {
      pattern: 'single',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'silence_1' },
        { type: 'hangup', delay: 12000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Jack. Pitching me software? You're barking up the wrong tree.",
      silence_1: "I don't care about the analytics. What's the big idea?",
      silence_2: "You're giving me a manual, I want a manifesto. Where's the story?",
      hangup: "Not interested. We're done. *click*", 
    },
    coachingNotes: {
      whatWorks: "Ditch the data. Paint a vivid, emotional picture of what your product allows his team to create.",
      commonMistake: "Listing features and integration capabilities. Silence = no story.",
      winningMove: "Say: 'I'm not selling software. I'm selling your creative team 10 hours a week back so they can actually focus on storytelling instead of spreadsheets.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 43. CHARLES WHITMORE (was Logan Roy)
  // ═════════════════════════════════════════════════════════════════
  'charles-whitmore-ceo': {
    id: 'charles-whitmore-ceo',
    name: 'Charles Whitmore',
    role: 'CEO',
    company: 'Waystar Energy',
    industry: 'energy',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/charles-whitmore-ceo.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 67,
    gender: 'male',
    ethnicity: 'Scottish',
    tags: ['intimidator', 'energy', 'hostile'],
    scenarioTitle: 'The Intimidator',
    scenarioDesc: "Charles is aggressive, impatient, and tests salespeople by being openly combative from second one. He's 67, Scottish, and built an empire on ruthlessness. He respects only one thing: someone who pushes back harder. He will hang up instantly if you show weakness. Silence = weakness.",
    goal: 'Get him to admit one flaw in his current system.',
    category: 'cold-call-mastery',
    personalityType: 'hostile',
    objectionStyle: 'no-time',
    silenceProfile: {
      pattern: 'instant',
      stages: [
        { type: 'hangup', delay: 4000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "What? Who is this and why are you wasting my time?",
      silence_1: "[short, dismissive laugh] Get to the point or I'm hanging up.",
      silence_2: "You're weak. Give me a reason to care.",
      hangup: "Idiots. *click*",
    },
    coachingNotes: {
      whatWorks: "Stand your ground. Push back with equal intensity and authority.",
      commonMistake: "Getting flustered, apologizing, or speeding up your pitch nervously. Silence = you're intimidated.",
      winningMove: "Say: 'I'm calling because your offshore rigs are leaking 5% of their efficiency to outdated maintenance tracking. That's why you should care.'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 44. ROSA DELUCA (was Carmela Soprano)
  // ═════════════════════════════════════════════════════════════════
  'rosa-deluca-director-of-compliance': {
    id: 'rosa-deluca-director-of-compliance',
    name: 'Rosa DeLuca',
    role: 'Director of Compliance',
    company: 'Soprano Pharmaceuticals',
    industry: 'pharma',
    companySize: 'midmarket',
    avatar: '/sales-negotiation/characters/cold-call-mastery/rosa-deluca-director-of-compliance.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 49,
    gender: 'female',
    ethnicity: 'Italian-American',
    tags: ['suspicious', 'pharma', 'compliance'],
    scenarioTitle: 'The Suspicious Director',
    scenarioDesc: "Rosa is highly protective of her department and assumes all salespeople are trying to trap her. She's 49, Italian-American, and has survived three FDA audits. She won't share anything over the phone. She views silence as a tactic to get her to reveal information. She hangs up fast.",
    goal: 'Get her to agree to receive a one-page case study.',
    category: 'cold-call-mastery',
    personalityType: 'curious-but-guarded',
    objectionStyle: 'deflects-to-email',
    silenceProfile: {
      pattern: 'double',
      stages: [
        { type: 'warn', delay: 6000, lineKey: 'silence_1' },
        { type: 'warn', delay: 12000, lineKey: 'silence_2' },
        { type: 'hangup', delay: 18000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Rosa. I didn't authorize this call. Put it in an email.",
      silence_1: "I don't discuss compliance issues over the phone with strangers. FDA rules.",
      silence_2: "You're asking too many questions. Just email it.",
      hangup: "Don't call this line again. *click*", 
},
    coachingNotes: {
      whatWorks: "Lower her defenses by explicitly stating you are not looking to uncover her compliance issues today.",
      commonMistake: "Asking 'pain' questions too early, which makes her feel interrogated. Silence = you're fishing for info.",
      winningMove: "Say: 'I completely understand. I'm not looking to audit your processes. I just want to email you a case study on how similar firms handled the new FDA guidelines. Fair?'"
    }
  },

  // ═════════════════════════════════════════════════════════════════
  // 45. SAM FISCHER (was Ben Wyatt)
  // ═════════════════════════════════════════════════════════════════
  'sam-fischer-vp-of-underwriting': {
    id: 'sam-fischer-vp-of-underwriting',
    name: 'Sam Fischer',
    role: 'VP of Underwriting',
    company: 'Pawnee Life & Casualty',
    industry: 'insurance',
    companySize: 'enterprise',
    avatar: '/sales-negotiation/characters/cold-call-mastery/sam-fischer-vp-of-underwriting.webp',
    difficulty: 'advanced',
    color: '#ef4444',
    age: 36,
    gender: 'male',
    ethnicity: 'German',
    tags: ['analytical', 'insurance', 'spreadsheeter'],
    scenarioTitle: 'The Spreadsheeter',
    scenarioDesc: "Sam loves numbers and logic. He will only respond to highly structured, mathematically sound arguments. He's 36, German, and has built every underwriting model his company uses. He respects precision and despises rounding errors. He gives you time because he wants the math to be right, but he won't tolerate sloppy thinking.",
    goal: 'Book a 20-minute discovery call.',
    category: 'cold-call-mastery',
    personalityType: 'analytical',
    objectionStyle: 'demands-proof',
    silenceProfile: {
      pattern: 'patient',
      stages: [
        { type: 'warn', delay: 10000, lineKey: 'silence_1' },
        { type: 'warn', delay: 20000, lineKey: 'silence_2' },
        { type: 'warn', delay: 25000, lineKey: 'lastChance' },
        { type: 'hangup', delay: 30000, lineKey: 'hangup' }
      ]
    },
    lines: {
      opening_a: "Sam here. Prove to me this isn't mathematically disadvantageous to my current operational model.",
      silence_1: "I need to see the underlying algorithms behind your claims.",
      silence_2: "Your value proposition is statistically insignificant.",
      lastChance: "I'll grant you one final calculation. Show me the actuarial tables or I'm terminating this call.",
      hangup: "The math doesn't check out. Goodbye."
    },
    coachingNotes: {
      whatWorks: "Speak to him in formulas. Offer to walk through a customized ROI calculator with him. He tolerates up to 30 seconds of silence if he thinks you're calculating.",
      commonMistake: "Using emotional appeals or qualitative benefits.",
      winningMove: "Say: 'Sam, I'm not going to guess your operational model. But I have a dynamic ROI calculator built specifically for underwriting teams. Can we spend 15 minutes plugging your variables in?'"
    }
  }

};

export const COLD_CALL_MASTERY_CHARACTERS = ALL_CHARACTERS;

export const DIFFICULTY_POOLS = {
  beginner:     ['henry-davies-retired-hobbyist-gardener','maria-santos-cafe-owner','ahmed-hassan-freelance-graphic-designer','linda-okafor-retired-schoolteacher','greg-thompson-long-haul-truck-driver','jenny-walsh-stay-at-home-mom','tomas-novak-part-time-musician-and-warehouse-worker','dorothy-fischer-retired-widow','derek-brown-college-student-and-part-time-barista','raj-kumar-rideshare-driver-and-online-seller','dr-arthur-pendelton-dean-of-admissions','zoe-mitchell-managing-editor','markus-thorne-vp-of-exploration','dr-susan-lin-director-of-r-and-d','marcus-reid-claims-manager'],
  intermediate: ['kevin-peterson-vp-of-supply-chain','claire-donovan-head-of-production','david-park-cto','helen-torres-director-of-administration','bradley-corbin-vp-of-sales','rebecca-cole-senior-buyer','mike-delfino-site-manager','victor-kane-senior-partner','diana-marsh-managing-director','james-ortiz-property-manager','dr-alan-foster-provost','natalie-cross-director-of-broadcasting','robert-vance-head-of-operations','dr-elena-voss-vp-of-clinical-trials','carl-hendricks-director-of-risk-management','cynthia-wallace-vp-of-logistics','tony-marchetti-plant-director','derek-holloway-ceo'],
  advanced:     ['patricia-morales-cfo','dr-marcus-webb-vp-of-procurement','pete-donovan-director-of-store-operations','victoria-ashford-vp-of-development','eddie-morra-managing-partner','trevor-blackwell-partner','linda-park-director-of-acquisitions','dr-neil-patel-chancellor','jack-sterling-vp-of-programming','charles-whitmore-ceo','rosa-deluca-director-of-compliance','sam-fischer-vp-of-underwriting'],
};

export const SCENARIO_ID = 'cold-call-mastery';

export default ALL_CHARACTERS;

export const coldCallMastery = ALL_CHARACTERS;